import React, { useEffect, useState } from 'react';
import type { ToggleModalProps } from '../components/modal/types';
import { Translate, useI18n } from '@whizz/react-i18n';
import { Button } from '../components';
import { GLOBAL_ICONS } from '../util/vars';
import { useModal } from './useModal';

type MessageType = 'success' | 'error' | 'duplicate' | null;
type CustomMessageType = { title: string, body: string | JSX.Element } | null;

const GenericModal = ({ toggle, children }: GenericModalProps): JSX.Element => {
  const { translate } = useI18n();

  return (
    <div className={'container'}>
      {children}
      <div className={'flex justify-end mt-lg'}>
        <Button
          isOutline
          align={'start'}
          icon={GLOBAL_ICONS.checkbox}
          onClick={() => toggle()}
        >
          {translate('components.BUTTONS.OK')}
        </Button>
      </div>
    </div>
  );
};

interface GenericModalProps extends ToggleModalProps {
  children: React.ReactNode;
}

export interface SuccessErrorModalProps extends ToggleModalProps {
  toggleMessageType: (message: MessageType, customMessage?: CustomMessageType) => void;
}

export const useSuccessErrorModal = (): [ React.ReactNode, SuccessErrorModalProps['toggleMessageType'] ] => {

  const { translate } = useI18n();
  const [ messageType, setMessageType ] = useState<MessageType>(null);
  const [ customMessage, setCustomMessage ] = useState<CustomMessageType>();

  const getMessages = () => {
    if (customMessage) {
      return { ...customMessage };
    }
    switch (messageType) {
      case 'success':
        return ({
          title: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTION_SUCCESS_MODAL.TITLE'),
          body: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTION_SUCCESS_MODAL.CONTENT'),
        });
      case 'error':
        return ({
          title: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTION_ERROR_MODAL.TITLE'),
          body: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTION_ERROR_MODAL.CONTENT'),
        });
      case 'duplicate':
        return ({
          title: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTION_DUPLICATE_MODAL.TITLE'),
          body: (
            <p className={'whitespace-pre-line'}>
              <Translate i18nKey={'pages.ROSTER_MANAGEMENT.STUDENTS.ACTION_DUPLICATE_MODAL.CONTENT'}>
                {/**
                 * eslint-disable-next-line @typescript-eslint/ban-ts-comment
                 * @ts-ignore **/ }
                <a href={`mailto:${translate('global.SUPPORT_EMAIL')}`} className={'underline'} target={'_blank'} />
              </Translate>
            </p>
          ),
        });
      default:
        return ({
          title: '',
          body: '',
        });
    }
  };

  useEffect(() => {
    const { title } = getMessages();
    setProps({
      title: title,
    });
  }, [ messageType, customMessage ]);

  const { body } = getMessages();

  const [ modal, toggle, setProps ] = useModal(
    (props: GenericModalProps) => (
      <GenericModal {...props}>
        { typeof body === 'string' ? (
          <p>{body}</p>
        ) : body }
      </GenericModal>
    ),
  );

  const toggleMessageType = (messageType: MessageType, customMessage: CustomMessageType = null) => {
    setCustomMessage(customMessage);
    setMessageType(messageType);
    toggle(true);
  };

  return [ modal, toggleMessageType ];
};
